import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Wind, Sun, AlertTriangle, Baby } from "lucide-react";

function statusOf(b) {
  if (!b.occupied) return "vacant";
  if (b.respiratory_support === "ventilator") return "vent";
  if (b.respiratory_support === "cpap") return "cpap";
  if (b.respiratory_support === "hfnc") return "hfnc";
  if (b.phototherapy) return "photo";
  return "occupied";
}

const STATUS_META = {
  vacant:   { label: "Vacant",    cls: "bg-vacant" },
  vent:     { label: "Ventilator",cls: "bg-critical" },
  cpap:     { label: "CPAP",      cls: "bg-warn" },
  hfnc:     { label: "HFNC",      cls: "bg-warn" },
  photo:    { label: "Phototherapy", cls: "bg-photo" },
  occupied: { label: "Occupied",  cls: "bg-muted text-muted-foreground" },
};

export default function BedMap() {
  const [beds, setBeds] = useState([]);
  useEffect(() => {
    let ok = true;
    const load = () => api.get("/beds").then(({ data }) => ok && setBeds(data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { ok = false; clearInterval(t); };
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const b of beds) { (g[b.nicu_area] ||= []).push(b); }
    return g;
  }, [beds]);

  return (
    <div className="space-y-8" data-testid="bed-map">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Ward layout</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Bed Map</h1>
        <p className="text-sm text-muted-foreground mt-1">Tap any bed to open the patient record.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <div key={k} className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${v.cls}`}>
            <span className="h-2 w-2 rounded-full bg-current" /> {v.label}
          </div>
        ))}
      </div>

      {Object.entries(grouped).map(([area, list]) => (
        <div key={area}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold">{area}</h3>
            <span className="text-xs text-muted-foreground">{list.filter(b => b.occupied).length} / {list.length} occupied</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {list.map((b) => {
              const st = statusOf(b);
              const meta = STATUS_META[st];
              const inner = (
                <div className={`bed-tile ${b.critical ? "border-red-500/60" : ""}`} data-testid={`bed-${b.bed_number}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">{b.bed_number}</div>
                    <div className={`h-6 px-2 inline-flex items-center rounded-full text-[10px] font-medium ${meta.cls}`}>{meta.label}</div>
                  </div>
                  <div className="mt-3 min-h-[52px]">
                    {b.occupied ? (
                      <>
                        <div className="font-medium truncate">{b.patient_name || "Patient"}</div>
                        <div className="text-xs text-muted-foreground truncate">{b.diagnosis || "—"}</div>
                        <div className="flex gap-1 mt-2">
                          {b.respiratory_support === "ventilator" && <Wind size={13} className="status-critical" />}
                          {b.respiratory_support === "cpap" && <Wind size={13} className="status-warn" />}
                          {b.respiratory_support === "hfnc" && <Wind size={13} className="status-warn" />}
                          {b.phototherapy && <Sun size={13} className="status-photo" />}
                          {b.critical && <AlertTriangle size={13} className="status-critical" />}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                        <Baby size={16} /> Ready
                      </div>
                    )}
                  </div>
                </div>
              );
              return b.patient_id
                ? <Link key={b.bed_number} to={`/patients/${b.patient_id}`}>{inner}</Link>
                : <div key={b.bed_number}>{inner}</div>;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
